import { ApiResponse, ApiServiceInterface, SyncEndpoints, UserServiceInterface } from '@hutsix/ngxh6';
import { concat, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';

export function syncConfigFactory(userService: UserServiceInterface, apiService: ApiServiceInterface, http: HttpClient): object {
  /**
   * Every synced endpoint is resolved against the offline queue via
   * `userService.user.id` inside the library, which throws a TypeError when no
   * user is loaded. A sync can run without one — it is kicked off from /login
   * before the account has been populated, and an in-flight sync outlives a
   * session that ends (e.g. token expiry clears the account and redirects to
   * /login). There is nothing to sync for a user that isn't there, so make the
   * tasks contribute no endpoints and skip their API calls instead.
   */
  const hasUser = (): boolean => !!userService.user?.id;

  return {
    /**
     * Add any endpoints that need to be synced offline here
     */
    endpoints: [],

    /**
     * Tasks are Observable[] and will be executed in sequence. You can perform any actions you want here.
     * The observable should return string[] of endpoints that will be added to the offline sync queue.
     */
    tasks: [
      new Observable<string[] | SyncEndpoints>(observer => {
        const endpoints = hasUser() ? ['/api/v1/areas?pagination=false', '/api/v1/users?pagination=false', '/api/v1/csp/users?pagination=false'] : [];
        let exclusions = [];
        observer.next(new SyncEndpoints({ exclusions, endpoints }));
        observer.complete();
      }),

      // Icon Sync
      new Observable<string[]>(observer => {
        if (!hasUser()) {
          observer.complete();
          return;
        }

        const iconEndpoints = ['/api/v1/csp/reasons?pagination=false', '/api/v1/csp/actions?pagination=false'];

        const apis = iconEndpoints.map(iconEndpoint => {
          return new Observable<void>(obs => {
            apiService
              .get({ url: iconEndpoint, useCache: true, refresh: true, displayErrors: false })
              .pipe(
                map((res: ApiResponse<any[]>) => {
                  if (res.type !== 'api' || !res.data.length) return;

                  const imgs = [];

                  res.data.forEach(i => {
                    if (i?.icon?.path) {
                      const isSvg = i?.icon?.path.endsWith('.svg');
                      const fileType = isSvg ? 'image/svg+xml' : 'image/png';

                      const headers = new HttpHeaders({ accept: fileType });

                      if (isSvg) {
                        imgs.push(http.get(i.icon.path, { headers, responseType: 'text' }));
                      } else {
                        imgs.push(http.get(i.icon.path, { headers, responseType: 'blob' }));
                      }
                    }
                  });

                  concat(...imgs).subscribe(
                    () => {},
                    () => {
                      obs.complete();
                    },
                    () => {
                      obs.complete();
                    },
                  );
                }),
              )
              .subscribe();
          });
        });

        concat(...apis).subscribe(
          () => {},
          () => observer.complete(),
          () => observer.complete(),
        );
      }),
    ],
  };
}
